import { type ChangeEvent, useMemo, useState } from 'react';

import { addDays, differenceInCalendarDays, max, subDays } from 'date-fns';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Label } from '@wf/sw-ui-kit';

import { LicenseField } from '../LicenseField';

import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import { MAX_NUMBER_MESSAGE } from '../../constants/validationMessages';
import { INTEGER_POSITIVE_REGEXP } from '../../../../constants/regex';
import { HeaderTabFields } from '../../types/fieldEnums';

import { maxNumOfDaysForExtensionSelector } from '../../store/selectors';

import styles from './HeaderTab.module.scss';

function ExtensionFields() {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const licenseAbbreviation = useLicenseAbbreviation();

  const maxNumOfDaysForExtension = useSelector(maxNumOfDaysForExtensionSelector);

  const [numberOfDays, setNumberOfDays] = useState<string | number>('');

  const validTo = useWatch({ name: HeaderTabFields.validTo });

  const minDate = useMemo(() => max([addDays(new Date(validTo as string), 1), new Date()]), [validTo]);

  const maxDate = useMemo(
    () => addDays(max([new Date(validTo as string), subDays(new Date(), 1)]), maxNumOfDaysForExtension),
    [maxNumOfDaysForExtension, validTo]
  );

  const handleDatePickerChange = (date: Date | null) => {
    const numberOfDaysNewValue = date ? differenceInCalendarDays(date, minDate) + 1 : '';

    setNumberOfDays(numberOfDaysNewValue);
  };

  const handleNumberOfDaysChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setNumberOfDays(value);

    if (!value || Number(value) > maxNumOfDaysForExtension) {
      setValue(HeaderTabFields.extendTo, null);
    } else {
      const newDate = addDays(minDate, Number(value) - 1);

      setValue(HeaderTabFields.extendTo, newDate);
    }
  };

  const numberOfDaysErrorMessage =
    Number(numberOfDays) > maxNumOfDaysForExtension
      ? t(MAX_NUMBER_MESSAGE, { max: maxNumOfDaysForExtension })
      : undefined;

  return (
    <>
      <LicenseField.DateField
        label={
          <Label
            helpText={t('Enter the new expiration date to extend the validity of the {{licenseAbbreviation}}', {
              licenseAbbreviation,
            })}
          >
            {t('Extend To')}
          </Label>
        }
        configName={HeaderTabFields.extendTo}
        onChange={handleDatePickerChange}
        placeholderText={t('Extend To')}
        name={HeaderTabFields.extendTo}
        className={styles.firstColumn}
        minDate={minDate}
        maxDate={maxDate}
      />
      <LicenseField.TextField
        label={
          <Label
            helpText={t(
              'If the {{licenseAbbreviation}} is still valid, you can specify the additional number of days of validity beyond the original expiration date. In the case of an expired {{licenseAbbreviation}}, indicate the additional number of days of validity to be counted from the day of extension',
              { licenseAbbreviation }
            )}
          >
            {t('Number of Days')}
          </Label>
        }
        errorMessage={numberOfDaysErrorMessage}
        onChange={handleNumberOfDaysChange}
        name={HeaderTabFields.numberOfDays}
        placeholder={t('Number of Days')}
        regexp={INTEGER_POSITIVE_REGEXP}
        value={numberOfDays}
      />
    </>
  );
}

export default ExtensionFields;
